import { ClientFigures } from "@/data/models/ClientAdministrationRow";
import { EMPTY_VALUE } from "@/utils/ClientAdministrationDetail";
import { formatDateToHoursAndMinuteString, formatDateTohDateMonthYearString } from "@/utils/DateHelper";

/** The blocks the figure columns are grouped into; reports and users stand alone without a heading. */
export type ClientListFigureGroup = "projects" | "reports" | "devices" | "users";

/**
 * The figure columns of the admin portal's Mandanten list from 960 px up (ticket 2610024), in their
 * order, grouped under "Projekte" and "Geräte" so the eye reads blocks instead of eight columns.
 * Below 960 px the list shows the totals in `CLIENT_LIST_COMPACT_FIGURE_COLUMNS` instead.
 */
export const CLIENT_LIST_FIGURE_COLUMNS: ReadonlyArray<{
  field: keyof ClientFigures;
  labelKey: string;
  group: ClientListFigureGroup;
}> = [
  { field: "numProjects", labelKey: "admin.clients.columns.currentProjects", group: "projects" },
  { field: "numOldProjects", labelKey: "admin.clients.columns.oldProjects", group: "projects" },
  { field: "numDeletedProjects", labelKey: "admin.clients.columns.deletedProjects", group: "projects" },
  { field: "numReports", labelKey: "admin.clients.columns.reports", group: "reports" },
  { field: "numCurrentPanels", labelKey: "admin.clients.columns.panels", group: "devices" },
  { field: "numCurrentComBoxes", labelKey: "admin.clients.columns.comBoxes", group: "devices" },
  { field: "numCurrentMinis", labelKey: "admin.clients.columns.minis", group: "devices" },
  { field: "numUsers", labelKey: "admin.clients.columns.users", group: "users" },
];

/**
 * The group headings above the figure columns, one per block, in column order. Reports and users
 * stand alone and have no heading; the entry still exists so each heading sits over its own columns.
 */
export const CLIENT_LIST_FIGURE_GROUPS: ReadonlyArray<{ group: ClientListFigureGroup; labelKey: string | null }> = [
  { group: "projects", labelKey: "admin.clients.columnGroups.projects" },
  { group: "reports", labelKey: null },
  { group: "devices", labelKey: "admin.clients.columnGroups.devices" },
  { group: "users", labelKey: null },
];

/**
 * The totals shown below 960 px instead of the breakdown (Taimoor, 2026-10-12): Projekte is Aktuell +
 * Alt (deleted projects left out, as everywhere else), Geräte the three device kinds, then Benutzer.
 */
export const CLIENT_LIST_COMPACT_FIGURE_COLUMNS: ReadonlyArray<{
  key: string;
  fields: ReadonlyArray<keyof ClientFigures>;
  labelKey: string;
}> = [
  { key: "projects", fields: ["numProjects", "numOldProjects"], labelKey: "admin.clients.columnGroups.projects" },
  {
    key: "devices",
    fields: ["numCurrentPanels", "numCurrentComBoxes", "numCurrentMinis"],
    labelKey: "admin.clients.columnGroups.devices",
  },
  { key: "users", fields: ["numUsers"], labelKey: "admin.clients.columns.users" },
];

/** Whether a column opens a new group, so the list can leave a gap before it. */
export function startsFigureGroup(index: number): boolean {
  return index === 0 || CLIENT_LIST_FIGURE_COLUMNS[index - 1].group !== CLIENT_LIST_FIGURE_COLUMNS[index].group;
}

/**
 * One figure of a client, or null when it is not known: not counted yet, or served by an IRES Cloud
 * build from before the figures existed.
 *
 * @param figures - the row's or the detail's figures, if any.
 * @param field - which figure.
 * @returns the figure, or null.
 */
export function clientFigure(figures: ClientFigures | undefined, field: keyof ClientFigures): number | null {
  const value = figures?.[field];
  return typeof value === "number" ? value : null;
}

/**
 * A figure cell as the list shows it: the sum of its fields, formatted. A 0 is left empty (Taimoor,
 * 2026-10-12), so the figures that are there stand out. "–" when any of the fields
 * is not known (not counted yet, or an IRES Cloud that does not serve it): a partial sum would pass
 * for the whole figure, and "–" must never read as 0.
 *
 * @param figures - the row's figures, if any.
 * @param fields - the figures added up in this cell; one for a breakdown column.
 * @param formatCount - the locale's number formatting.
 * @returns the cell's text.
 */
export function clientFigureCellText(
  figures: ClientFigures | undefined,
  fields: ReadonlyArray<keyof ClientFigures>,
  formatCount: (value: number) => string
): string {
  const values = fields.map((field) => clientFigure(figures, field));

  if (values.some((value) => value === null)) {
    return EMPTY_VALUE;
  }

  const total = (values as number[]).reduce((sum, value) => sum + value, 0);
  return total === 0 ? "" : formatCount(total);
}

/**
 * When the figures were counted, as the "Stand" shows it: the time alone on the same day, the date
 * and time otherwise (a Monday morning reads Sunday 20:07, and a stalled job must not pass for today).
 *
 * @param updatedAt - epoch ms of the last run, or null.
 * @param now - the current time.
 * @returns the text, or null when there has been no run.
 */
export function figuresUpdatedAtText(updatedAt: number | null, now: Date): string | null {
  if (updatedAt === null) {
    return null;
  }

  const date = new Date(updatedAt);
  const time = formatDateToHoursAndMinuteString(date);
  const sameDay = date.toDateString() === now.toDateString();

  return sameDay ? time : `${formatDateTohDateMonthYearString(date)} ${time}`;
}
